import React from "react";
import { Link } from "react-router-dom";

const regionData = {
  dubai: {
    region: "Dubai",
    country: "UAE",
    flag: "🇦🇪",
    hero: "CTO & AI Product Builder for Dubai Startups",
    intro: `Dubai's startup ecosystem is one of the fastest-growing in the world, powered by government initiatives like Dubai 2040 and the thriving DIFC tech hub. Companies in Dubai need a technology partner who understands both the pace of regional growth and the technical depth required to build world-class products.`,
    body: `
I'm Sami — a CTO and AI product builder based in Pakistan, serving founders and companies across Dubai, Abu Dhabi, and the wider UAE. With over a decade of experience building mobile apps, web platforms, and AI-driven automation systems, I help Dubai-based startups move from idea to market-ready product at 10x the speed of traditional development teams.

**Who I Work With in Dubai**

- Early-stage startups looking for a fractional CTO to architect their tech stack
- Funded companies in DIFC or Dubai Internet City needing to scale engineering
- Real estate, logistics, and fintech companies wanting AI automation
- Founders building consumer apps for the UAE market

**Services I Offer Dubai Clients**

1. **Fractional CTO** — Full technical leadership without the full-time cost. I define your architecture, manage your team, and align technology with your business goals.
2. **AI Product Development** — Custom AI agents, chatbots (GPT-4/Claude), and RAG systems tailored to your business workflows.
3. **Mobile App Development** — React Native apps for iOS and Android with App Store and Google Play deployment.
4. **Low-Code Automation** — n8n, Make, and Zapier integrations to automate your operations.

**Why Dubai Companies Hire Remote Developers from Pakistan**

Pakistan produces some of the highest-quality software engineers in the world at a fraction of the cost of hiring locally in the UAE. I bridge that gap by offering Dubai-standard quality, communication, and accountability — with the cost advantage of a Pakistan-based team.

If you're a company in Dubai looking for a CTO, an AI developer, or a mobile app team, let's talk.`,
    keywords: "CTO Dubai, AI developer Dubai, mobile app development Dubai UAE, fractional CTO Dubai, software developer Dubai, startup tech partner Dubai",
    cta: "Get in Touch on WhatsApp",
    ctaLink: "https://wa.me/923070145744?text=Hi%20Sami%2C%20I%20found%20your%20portfolio%20and%20want%20to%20discuss%20a%20project.",
  },
  uae: {
    region: "UAE",
    country: "United Arab Emirates",
    flag: "🇦🇪",
    hero: "AI Software Developer for UAE Companies & Startups",
    intro: `The United Arab Emirates is building towards a digital-first economy through UAE Vision 2031. Businesses across Abu Dhabi, Sharjah, and Ajman are investing in AI, mobile, and cloud technology to stay competitive.`,
    body: `
I'm Sami — a full-stack AI product builder and CTO with 10+ years of experience building technology for international clients. I work with UAE-based companies remotely from Pakistan, delivering high-quality software with the professionalism and responsiveness of a local team.

**My Technical Stack for UAE Clients**

- **Frontend**: React.js, React Native, Next.js
- **Backend**: Node.js, NestJS, Python (FastAPI)
- **AI/ML**: OpenAI GPT-4, Claude, LangChain, RAG systems, autonomous agents
- **Automation**: n8n, Make (Integromat), Zapier
- **Cloud**: Firebase, AWS, Vercel, Supabase

**Industries I've Served in the UAE Market**

- Real estate & proptech apps
- Logistics and fleet management platforms
- Health & wellness mobile applications
- Social and community platforms
- Fintech and payments

**Why Remote Pakistan Developers Are Ideal for UAE Projects**

With timezone proximity (+1 hour from UAE to PKT), same-day communication, and significantly lower hourly rates, Pakistan-based engineers are the smart choice for UAE companies wanting quality without the overhead of local hiring.

Let's build your next product together.`,
    keywords: "UAE software developer, AI developer UAE, mobile app development UAE, React Native UAE, CTO UAE, tech consultant UAE",
    cta: "Start a Project",
    ctaLink: "https://wa.me/923070145744?text=Hi%20Sami%2C%20I%20found%20your%20portfolio%20and%20want%20to%20discuss%20a%20project.",
  },
  "saudi-arabia": {
    region: "Saudi Arabia",
    country: "Saudi Arabia",
    flag: "🇸🇦",
    hero: "AI Developer for Saudi Arabia Startups & Vision 2030 Companies",
    intro: `Saudi Arabia's Vision 2030 has created one of the most exciting tech investment environments in the world. From Riyadh's thriving startup scene to NEOM's futuristic developments, Saudi companies are racing to digitize and automate their operations.`,
    body: `
I'm Sami — a CTO and AI product builder helping Saudi Arabia companies build intelligent software. From Riyadh to Jeddah and KAUST spinouts, I work with founders and enterprise teams who need to ship products fast, integrate AI, and scale their platforms.

**How I Help Saudi Companies**

Startups in Riyadh hiring AI developers often face two problems: local talent is expensive and scarce, and offshore teams lack understanding of the Gulf market. I solve both. I'm a remote developer who understands GCC business culture, Arabic UX considerations, and the pace Vision 2030 demands.

**Projects Relevant to Saudi Vision 2030**

- Smart city tech: real-time data dashboards and IoT integrations
- Gig economy and logistics platforms (similar to Noon, Jahez)
- AI-powered HR and recruitment tools
- Arabic language AI chatbots for customer service
- E-learning and EdTech platforms

**My Process for Saudi Arabia Clients**

1. Discovery call to understand your product goals
2. Technical architecture document delivered within 48 hours
3. Agile development in 2-week sprints
4. Regular video check-ins aligned with Saudi working hours
5. Full code handover and documentation

If you're a company in Riyadh, Jeddah, or KAUST looking for an AI developer or fractional CTO, let's connect.`,
    keywords: "Saudi Arabia AI developer, CTO Riyadh, software developer Saudi Arabia, AI startup Saudi, Vision 2030 tech developer, mobile app Riyadh",
    cta: "Discuss Your Project",
    ctaLink: "https://wa.me/923070145744?text=Hi%20Sami%2C%20I%20found%20your%20portfolio%20and%20want%20to%20discuss%20a%20project.",
  },
  pakistan: {
    region: "Pakistan",
    country: "Pakistan",
    flag: "🇵🇰",
    hero: "Top Software Engineer & AI Product Builder in Pakistan",
    intro: `Pakistan is home to some of South Asia's most talented software engineers. With a growing tech ecosystem in Lahore, Karachi, and Islamabad, Pakistani developers are increasingly trusted by global companies for building complex, scalable products.`,
    body: `
I'm Sami, a CTO and AI product builder based in Lahore, Pakistan. With over 10 years of hands-on experience, I've shipped more than 50 projects for clients in the USA, UAE, Europe, and Pakistan — spanning mobile apps, AI platforms, and enterprise automation.

**Why Pakistani Companies & Startups Choose Me**

- Deep understanding of local market challenges and user behavior
- Experience with Pakistani fintech regulations and mobile money integrations
- Affordable rates with enterprise-grade quality
- Agile team based in Lahore with fast turnaround

**Services for Pakistan Market**

1. **Mobile App Development** — React Native apps for Pakistani consumers with Easypaisa/JazzCash integrations
2. **SaaS Platforms** — B2B web platforms for Pakistani SMEs and enterprises
3. **AI Automation** — Automating back-office tasks for businesses in Lahore, Karachi, Islamabad
4. **CTO Advisory** — Helping Pakistani startups structure their engineering teams and technology roadmap

**Case Studies (Pakistan)**

- Delivered a ride-hailing app for the Pakistan market with real-time GPS tracking
- Built an automated HR recruitment system for a Lahore-based enterprise
- Developed a mobile learning app used by 10,000+ students across Pakistan

Whether you're a startup in Lahore or an established business in Karachi looking to digitize, I'm ready to help.`,
    keywords: "software developer Pakistan, CTO Pakistan Lahore, AI developer Karachi, mobile app development Pakistan, React Native Pakistan, tech startup Pakistan",
    cta: "Let's Build Together",
    ctaLink: "https://wa.me/923070145744?text=Hi%20Sami%2C%20I%20found%20your%20portfolio%20and%20want%20to%20discuss%20a%20project.",
  },
};

const RegionPage = ({ regionKey }) => {
  const data = regionData[regionKey];

  if (!data) return <div className="text-white p-10">Region not found</div>;

  // Convert markdown-like **bold** to <strong>
  const renderBody = (text) => {
    return text
      .split("\n")
      .filter((line) => line.trim() !== "")
      .map((line, i) => {
        const isH2 = line.startsWith("**") && line.endsWith("**") && line.length > 4;
        const isNumbered = /^\d+\./.test(line.trim());
        const isDash = line.trim().startsWith("- ");

        if (isH2) {
          const clean = line.replace(/\*\*/g, "");
          return (
            <h2 key={i} className="text-xl font-bold text-[#b47cfd] mt-8 mb-3">
              {clean}
            </h2>
          );
        }
        if (isDash) {
          return (
            <li key={i} className="text-gray-300 ml-4 list-disc mb-1">
              {line.replace(/^- /, "").replace(/\*\*(.*?)\*\*/g, (_, m) => m)}
            </li>
          );
        }
        if (isNumbered) {
          const parts = line.split(/\*\*(.*?)\*\*/g);
          return (
            <p key={i} className="text-gray-300 mb-2">
              {parts.map((part, j) =>
                j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part
              )}
            </p>
          );
        }
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} className="text-gray-300 leading-relaxed mb-3">
            {parts.map((part, j) =>
              j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part
            )}
          </p>
        );
      });
  };

  return (
    <div className="min-h-screen text-gray-300 px-6 pt-10 pb-20 max-w-4xl mx-auto">
      {/* Hero */}
      <div className="mb-10">
        <span className="text-4xl">{data.flag}</span>
        <h1 className="text-3xl md:text-5xl font-bold text-[#b47cfd] mt-4 mb-4 leading-tight">
          {data.hero}
        </h1>
        <p className="text-gray-300 text-lg leading-relaxed">{data.intro}</p>
      </div>

      {/* Body Content */}
      <article className="prose-invert max-w-none">
        {renderBody(data.body)}
      </article>

      {/* CTA Block */}
      <div className="mt-12 bg-gradient-to-r from-gray-800 to-gray-900 border border-[#b47cfd]/30 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">
          Ready to Start a Project?
        </h2>
        <p className="text-gray-400 mb-6">
          I'm currently available for new projects in {data.region}. Let's talk.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={data.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#1da851] transition-all duration-300 inline-flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {data.cta}
          </a>
          <Link
            to="/work"
            className="bg-transparent border border-[#b47cfd] text-[#b47cfd] px-8 py-3 rounded-full font-semibold hover:bg-[#b47cfd] hover:text-gray-900 transition-all duration-300"
          >
            View My Work
          </Link>
        </div>
      </div>

      {/* Internal Links */}
      <div className="mt-12 border-t border-gray-800 pt-8">
        <p className="text-gray-500 text-sm mb-4">Explore my services for other regions:</p>
        <div className="flex flex-wrap gap-3">
          {regionKey !== "dubai" && (
            <Link to="/dubai-cto" className="text-[#b47cfd] text-sm hover:underline">🇦🇪 CTO for Dubai Startups</Link>
          )}
          {regionKey !== "uae" && (
            <Link to="/uae-software-developer" className="text-[#b47cfd] text-sm hover:underline">🇦🇪 UAE Software Developer</Link>
          )}
          {regionKey !== "saudi-arabia" && (
            <Link to="/saudi-arabia-ai-developer" className="text-[#b47cfd] text-sm hover:underline">🇸🇦 Saudi Arabia AI Developer</Link>
          )}
          {regionKey !== "pakistan" && (
            <Link to="/pakistan-software-engineer" className="text-[#b47cfd] text-sm hover:underline">🇵🇰 Pakistan Software Engineer</Link>
          )}
          <Link to="/services" className="text-gray-400 text-sm hover:underline">→ All Services</Link>
          <Link to="/" className="text-gray-400 text-sm hover:underline">→ Home</Link>
        </div>
      </div>
    </div>
  );
};

export default RegionPage;
